import { useEffect, useState } from "react";
import api from "@/lib/api";
import { D, designError, label, resetDesignMe, useDesignMe } from "@/lib/designApi";
import { Field, NoAccess, PageHeader, Section, SimpleSelect } from "@/components/design/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function DesignSettings() {
  const { me, can, error: meErr } = useDesignMe();
  const [data, setData] = useState(null);
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const load = () => api.get(`${D}/settings`).then((r) => { setData(r.data); setS(r.data.settings); }).catch((e) => setErr(designError(e)));
  useEffect(() => { load(); }, []);
  if (meErr || (me && !me.has_access)) return <NoAccess error={meErr} />;
  if (err) return <NoAccess error={err} />;
  if (!s || !me) return <div className="text-sm text-slate-500">Loading…</div>;
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const roles = me.enums.functional_roles;
  const save = async () => {
    try {
      const body = { ...s, max_upload_mb: Number(s.max_upload_mb), part_number_digits: Number(s.part_number_digits), duplicate_threshold: Number(s.duplicate_threshold) };
      await api.put(`${D}/settings`, body);
      resetDesignMe();
      toast.success("Settings saved");
      load();
    } catch (e) { toast.error(designError(e)); }
  };
  const togglePerm = (perm, role) => setS((x) => {
    const cur = new Set(x.permissions[perm] || []);
    if (cur.has(role)) cur.delete(role); else cur.add(role);
    return { ...x, permissions: { ...x.permissions, [perm]: [...cur] } };
  });
  const setRole = async (u, role) => {
    try {
      await api.post(`${D}/settings/user-role`, { user_id: u.id, functional_role: role === "default" ? null : role });
      toast.success(`Updated ${u.email}`);
      load();
    } catch (e) { toast.error(designError(e)); }
  };
  return (
    <div className="space-y-6 max-md:space-y-3" data-testid="design-settings-page">
      <PageHeader title="Design Settings" subtitle="Numbering, revisions, files, approvals and the role permission matrix">
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} disabled={!can("settings")} data-testid="design-settings-save">Save settings</Button>
      </PageHeader>
      <div className="grid lg:grid-cols-2 gap-4">
        <Section title="Numbering & revisions">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Part number format ({prefix} {seq} {yy} {yyyy})"><Input value={s.part_number_format} onChange={(e) => set("part_number_format", e.target.value)} /></Field>
            <Field label="Sequence digits"><Input type="number" min="3" max="10" value={s.part_number_digits} onChange={(e) => set("part_number_digits", e.target.value)} /></Field>
            <Field label="Document number format"><Input value={s.document_number_format} onChange={(e) => set("document_number_format", e.target.value)} /></Field>
            <Field label="Revision scheme"><SimpleSelect value={s.revision_scheme} onChange={(v) => set("revision_scheme", v)} options={[{ value: "alpha", label: "A, B, C … AA" }, { value: "numeric", label: "00, 01, 02 …" }]} /></Field>
            <Field label="Duplicate-item threshold (0.5 – 1)"><Input type="number" step="0.01" min="0.5" max="1" value={s.duplicate_threshold} onChange={(e) => set("duplicate_threshold", e.target.value)} /></Field>
          </div>
        </Section>
        <Section title="Files & approvals">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Max upload size (MB)"><Input type="number" min="1" max="500" value={s.max_upload_mb} onChange={(e) => set("max_upload_mb", e.target.value)} data-testid="settings-max-mb" /></Field>
            <Field label="PDF watermark text"><Input value={s.watermark_text} onChange={(e) => set("watermark_text", e.target.value)} /></Field>
            <Field label="Allowed file extensions" className="col-span-2"><Input value={s.allowed_extensions.join(", ")} onChange={(e) => set("allowed_extensions", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} /></Field>
          </div>
          <div className="space-y-2 mt-3">
            {[["esign_require_password", "Require password re-entry (e-signature) on check / approve / release"],
              ["allow_self_approval", "Allow maker to check / approve own records (not recommended)"],
              ["require_document_for_item_release", "Item release requires an approved linked drawing"],
              ["watermark_pdfs", "Stamp / watermark PDFs on download"],
              ["email_notifications", "Send email notifications (in-app always on)"]].map(([k, t]) => (
              <label key={k} className="flex items-center gap-3 text-sm"><Switch checked={!!s[k]} onCheckedChange={(v) => set(k, v)} />{t}</label>
            ))}
          </div>
          <div className="text-[11px] text-slate-500 mt-3">Files are stored in the design vault outside the web root and served only through permission-checked, audited downloads.</div>
        </Section>
      </div>
      <Section title="Item categories & part-number prefixes" right={<Button size="sm" variant="outline" onClick={() => set("item_categories", [...s.item_categories, { code: "", label: "", prefix: "", erp_type: "RAW" }])}><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>}>
        <table className="w-full text-sm"><thead><tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 border-b"><th className="py-1.5">Code</th><th>Label</th><th>Prefix</th><th>ERP item type on publish</th><th /></tr></thead>
          <tbody>{s.item_categories.map((c, i) => (
            <tr key={i} className="border-b border-slate-50">
              {["code", "label", "prefix"].map((k) => <td key={k} className="py-1 pr-2"><Input value={c[k]} onChange={(e) => set("item_categories", s.item_categories.map((x, j) => (j === i ? { ...x, [k]: k === "label" ? e.target.value : e.target.value.toUpperCase() } : x)))} /></td>)}
              <td className="pr-2"><SimpleSelect value={c.erp_type} onChange={(v) => set("item_categories", s.item_categories.map((x, j) => (j === i ? { ...x, erp_type: v } : x)))} options={["FG", "SFG", "RAW", "MS", "IMP", "FAB", "PWD", "ASM", "TEST", "FI"]} /></td>
              <td><button className="text-slate-400 hover:text-red-600" onClick={() => set("item_categories", s.item_categories.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></button></td>
            </tr>
          ))}</tbody></table>
      </Section>
      <Section title="Permission matrix (functional role × action)">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="design-perm-matrix">
            <thead><tr className="text-left text-[10px] uppercase tracking-[0.12em] text-slate-500 border-b"><th className="py-1.5 pr-3">Action</th>
              {roles.map((r) => <th key={r} className="px-1 text-center">{me.enums.functional_role_labels[r]}</th>)}</tr></thead>
            <tbody>{Object.keys(me.enums.permission_labels).map((p) => (
              <tr key={p} className="border-b border-slate-50">
                <td className="py-1 pr-3">{me.enums.permission_labels[p]}</td>
                {roles.map((r) => <td key={r} className="text-center"><input type="checkbox" checked={(s.permissions[p] || []).includes(r)} onChange={() => togglePerm(p, r)} disabled={p === "settings" && r === "admin"} /></td>)}
              </tr>
            ))}</tbody>
          </table>
        </div>
      </Section>
      <Section title="User → design role">
        <div className="text-xs text-slate-500 mb-2">Default comes from the ERP role (Users &amp; Roles). Override here to e.g. make a production manager a Checker inside Design only.</div>
        <div className="max-h-96 overflow-auto">
          <table className="w-full text-sm"><thead><tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 border-b"><th className="py-1.5">User</th><th>ERP role</th><th>Design role</th><th>Override</th></tr></thead>
            <tbody>{data.users.map((u) => (
              <tr key={u.id} className="border-b border-slate-50">
                <td className="py-1">{u.name} <span className="text-xs text-slate-500">{u.email}</span></td>
                <td className="text-xs">{label(u.role)}</td>
                <td className="text-xs font-semibold">{u.functional_role ? me.enums.functional_role_labels[u.functional_role] : <span className="text-slate-400">no access</span>}</td>
                <td className="w-48"><SimpleSelect value={u.override || "default"} onChange={(v) => setRole(u, v)}
                  options={[{ value: "default", label: "Default (from ERP role)" }, ...roles.map((r) => ({ value: r, label: me.enums.functional_role_labels[r] }))]} /></td>
              </tr>
            ))}</tbody></table>
        </div>
      </Section>
    </div>
  );
}
